import { useEffect, useMemo, useState } from "react";

const DEFAULT_ITEMS_PER_PAGE = 8;
const DEFAULT_MAX_VISIBLE_PAGES = 5;

export const usePagination = (
    items = [],
    itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
    maxVisiblePages = DEFAULT_MAX_VISIBLE_PAGES
) => {
    const [currentPage, setCurrentPage] = useState(1);

    const totalItems = Array.isArray(items) ? items.length : 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const paginatedItems = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return items.slice(startIndex, endIndex);
    }, [items, currentPage, itemsPerPage]);

    const currentBlock = Math.ceil(currentPage / maxVisiblePages);
    const startPage = (currentBlock - 1) * maxVisiblePages + 1;
    const endPage = Math.min(startPage + maxVisiblePages - 1, totalPages);

    const visiblePages = useMemo(() => {
        const pages = [];
        for (let page = startPage; page <= endPage; page += 1) {
            pages.push(page);
        }
        return pages;
    }, [startPage, endPage]);

    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    const goToPage = (page) => {
        const safePage = Math.min(Math.max(page, 1), totalPages);
        setCurrentPage(safePage);
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const resetPagination = () => {
        setCurrentPage(1);
    };

    return {
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        paginatedItems,
        visiblePages,
        startItem,
        endItem,
        goToPage,
        goToPreviousPage,
        goToNextPage,
        resetPagination,
        hasPreviousPage: currentPage > 1,
        hasNextPage: currentPage < totalPages,
    };
};